import { useParams } from "react-router-dom";
import { Label } from "@groot/ui/label";
import { OrderStatusBadge, PaymentStatusBadge } from "@/components/status-badges";
import { useCustomer, useUpdateCustomer } from "@/pages/customer/hooks/useCustomers";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Textarea } from "@groot/ui/textarea";
import { toast } from "sonner";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  AtSign,
  MapPin,
  ArrowLeft,
  Edit2,
  Save,
  Plus,
  Eye,
  UserX,
  ShoppingCart,
} from "lucide-react";
import { CreateOrderDialog } from "@/pages/order/components/CreateOrderDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Skeleton } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const customerId = parseInt(id || "0", 10);
  const { data: customer, isLoading } = useCustomer(customerId);
  const updateCustomer = useUpdateCustomer();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    email: "",
    instagramHandle: "",
    address: "",
    notes: "",
  });

  if (isLoading) {
    return (
      <PageLayout title="Customer" description="Loading...">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!customer) {
    return (
      <PageLayout title="Customer" description="Not found">
        <EmptyState
          icon={UserX}
          title="Customer not found"
          description="This customer may have been deleted or the link is invalid."
          action={
            <Button variant="outline" asChild>
              <Link to="/customers">
                <ArrowLeft className="size-4" />
                Back to Customers
              </Link>
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const handleEdit = () => {
    setFormData({
      name: customer.name,
      mobileNumber: customer.mobileNumber ?? "",
      email: customer.email || "",
      instagramHandle: customer.instagramHandle || "",
      address: customer.address || "",
      notes: customer.notes || "",
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateCustomer.mutateAsync({
        id: customerId,
        data: {
          ...formData,
          email: formData.email || undefined,
          instagramHandle: formData.instagramHandle || undefined,
          address: formData.address || undefined,
          notes: formData.notes || undefined,
        },
      });
      toast.success("Success", { description: "Customer updated" });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to update customer" });
    }
  };

  return (
    <PageLayout
      title={customer.name}
      description="Customer details"
      actions={
        <div className="flex flex-wrap gap-2">
          <CreateOrderDialog
            preselectedCustomerId={customerId}
            trigger={
              <Button variant="outline">
                <Plus className="size-4" />
                Create Order
              </Button>
            }
          />
          {isEditing ? (
            <Button onClick={handleSave} disabled={updateCustomer.isPending}>
              <Save className="size-4" />
              Save
            </Button>
          ) : (
            <Button variant="outline" onClick={handleEdit}>
              <Edit2 className="size-4" />
              Edit
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/customers">
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mobile Number</Label>
                  <Input
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({ ...formData, mobileNumber: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram Handle</Label>
                  <Input
                    value={formData.instagramHandle}
                    onChange={(e) => setFormData({ ...formData, instagramHandle: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center gap-2">
                  <Phone className="size-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">{customer.mobileNumber ?? "—"}</span>
                </div>
                {customer.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="size-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{customer.email}</span>
                  </div>
                )}
                {customer.instagramHandle && (
                  <div className="flex items-center gap-2">
                    <AtSign className="size-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{customer.instagramHandle}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-center gap-2 md:col-span-2">
                    <MapPin className="size-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{customer.address}</span>
                  </div>
                )}
                {customer.notes && (
                  <div className="md:col-span-2 pt-2 border-t">
                    <p className="text-sm text-muted-foreground">{customer.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Order History</CardTitle>
            <CreateOrderDialog
              preselectedCustomerId={customerId}
              trigger={
                <Button variant="outline" size="sm">
                  <Plus className="size-4" />
                  Create Order
                </Button>
              }
            />
          </CardHeader>
          <CardContent>
            {customer.orders.length === 0 ? (
              <EmptyState
                icon={ShoppingCart}
                title="No orders yet"
                description="Create an order for this customer to get started."
                action={
                  <CreateOrderDialog
                    preselectedCustomerId={customerId}
                    trigger={
                      <Button size="sm">
                        <Plus className="size-4" />
                        Create Order
                      </Button>
                    }
                  />
                }
              />
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <ul className="md:hidden divide-y divide-border">
                  {customer.orders.map((order) => (
                    <li key={order.id}>
                      <Link
                        to={`/orders/${order.id}`}
                        className="flex items-start justify-between gap-3 px-4 py-3"
                      >
                        <div className="min-w-0 space-y-1.5">
                          <div className="text-sm font-medium text-foreground">
                            {order.orderNumber}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <OrderStatusBadge status={order.status} />
                            <PaymentStatusBadge status={order.paymentStatus} />
                            <span className="text-xs text-muted-foreground">
                              {new Date(order.orderDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <span className="shrink-0 text-sm font-medium text-foreground">
                          ₹{order.total}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>

                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Order #</TableHead>
                        <TableHead className="hidden sm:table-cell">Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="hidden sm:table-cell">Payment</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-12" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customer.orders.map((order) => (
                        <TableRow key={order.id} className="hover:bg-muted/50 border-border">
                          <TableCell className="text-sm text-foreground">
                            <Link
                              to={`/orders/${order.id}`}
                              className="font-medium hover:text-primary"
                            >
                              {order.orderNumber}
                            </Link>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground hidden sm:table-cell">
                            {new Date(order.orderDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <OrderStatusBadge status={order.status} />
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <PaymentStatusBadge status={order.paymentStatus} />
                          </TableCell>
                          <TableCell className="text-sm text-foreground text-right font-medium">
                            ₹{order.total}
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" asChild>
                              <Link to={`/orders/${order.id}`}>
                                <Eye className="size-4" />
                                <span className="sr-only">View order</span>
                              </Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
